/**
 * © 2026 Madar.iq — جميع الحقوق محفوظة
 * هذا الملف جزء من منظومة Madar.iq البرمجية.
 * يُحظر نسخه أو تعديله أو توزيعه دون إذن كتابي.
 * All Rights Reserved — Unauthorized use is prohibited.
 */
import React, { useState, useEffect, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
    FiArrowRight,
    FiArrowLeft,
    FiPhone,
    FiMail,
    FiMessageCircle,
    FiSend,
    FiShoppingBag,
    FiCheckCircle,
    FiDollarSign,
    FiTrendingUp,
    FiTrash2,
    FiMoreHorizontal
} from "react-icons/fi";
import { useUI } from "../../theme/ThemeProvider";
import clsx from "clsx";
import PageContainer from "../../components/PageContainer";
import ResponsiveTable from "../../components/ResponsiveTable";
import { pickActiveDeliveryCompany } from "../../../Stores/utils/storeApi";

export default function CustomerDetails() {
    const { customerName } = useParams();
    const navigate = useNavigate();
    const { theme, lang } = useUI();
    const isDark = theme === "dark";
    const isArabic = lang === "ar";

    const [loading, setLoading] = useState(true);
    const [orders, setOrders] = useState([]);
    const [customer, setCustomer] = useState(null);
    const [statusFilter, setStatusFilter] = useState("all");
    const [deliveryMap, setDeliveryMap] = useState({});

    const baseURL = (
        process.env.REACT_APP_API_BASE_URL || "https://madar-api.madar.iq/api"
    ).replace(/\/+$/, "");

    const STATUS_TABS = [
        { key: "all", label_ar: "الكل", label_en: "All" },
        { key: "new", label_ar: "جديد", label_en: "New" },
        { key: "preparing", label_ar: "جاري التجهيز", label_en: "Preparing" },
        { key: "ready", label_ar: "جاهز", label_en: "Ready" },
        { key: "delivering", label_ar: "جاري التوصيل", label_en: "Delivering" },
        { key: "delivered", label_ar: "مكتمل", label_en: "Completed" },
        { key: "returned", label_ar: "مسترجع", label_en: "Returned" },
        { key: "partially_returned", label_ar: "مسترجع جزئيًا", label_en: "Partially Returned" },
        { key: "cancelled", label_ar: "ملغي", label_en: "Cancelled" },
        { key: "failed", label_ar: "فشل التوصيل", label_en: "Delivery Failed" },
    ];

    const ORDER_STATUSES = [
        { id: 1, ar: "جديد", en: "New", key: "new", color: "bg-blue-100 text-blue-700 border-blue-200" },
        { id: 2, ar: "جاري التجهيز", en: "Preparing", key: "preparing", color: "bg-yellow-100 text-yellow-700 border-yellow-200" },
        { id: 3, ar: "جاهز", en: "Ready", key: "ready", color: "bg-purple-100 text-purple-700 border-purple-200" },
        { id: 4, ar: "جاري التوصيل", en: "In Delivery", key: "delivering", color: "bg-indigo-100 text-indigo-700 border-indigo-200" },
        { id: 5, ar: "مكتمل", en: "Completed", key: "delivered", color: "bg-green-100 text-green-700 border-green-200" },
        { id: 6, ar: "مسترجع", en: "Returned", key: "returned", color: "bg-orange-100 text-orange-700 border-orange-200" },
        { id: 8, ar: "مسترجع جزئيًا", en: "Partially Returned", key: "partially_returned", color: "bg-teal-100 text-teal-700 border-teal-200" },
        { id: 7, ar: "ملغي", en: "Cancelled", key: "cancelled", color: "bg-red-100 text-red-700 border-red-200" },
        { id: 9, ar: "فشل التوصيل", en: "Delivery Failed", key: "failed", color: "bg-black text-white border-gray-800" },
    ];

    const normalizeStatusKey = (raw) => {
        if (!raw) return "new";
        const v = raw.toString().toLowerCase().trim();
        const found = ORDER_STATUSES.find(s =>
            s.ar === raw ||
            s.en.toLowerCase() === v ||
            s.key === v
        );
        return found ? found.key : "new";
    };

    const parseOrderDate = (value) => {
        if (!value) return "-";
        const fixed = value.toString().replace(/\s+/g, " ").replace(/(\d)(AM|PM)/i, "$1 $2");
        const d = Date.parse(fixed);
        if (!isNaN(d)) {
            return new Date(d).toISOString().split('T')[0];
        }
        return value;
    };

    useEffect(() => {
        const fetchData = async () => {
            try {
                setLoading(true);
                const tenantId = localStorage.getItem("tenantId");
                const token = localStorage.getItem("authToken");

                const res = await fetch(`${baseURL}/GetTenantOrders?tenantId=${tenantId}&statusId=-1`, {
                    headers: { Authorization: token ? `Bearer ${token}` : "" }
                });

                if (res.ok) {
                    const data = await res.json();
                    // Filter by name (or phone for backward/deep linking compatibility)
                    const customerOrders = data.filter(o => {
                        const p = (o.customerPhone1 || "").trim();
                        const n = (o.customerName || "").trim();
                        return n === customerName || encodeURIComponent(n) === customerName || p === customerName || p === `0${customerName}` || customerName === `0${p}`;
                    });
                    setOrders(customerOrders);

                    // Fetch delivery info for all unique cityIds
                    const uniqueCityIds = [...new Set(customerOrders.map(o => o.cityId).filter(Boolean))];
                    if (uniqueCityIds.length > 0) {
                        const map = {};
                        await Promise.allSettled(
                            uniqueCityIds.map(cId =>
                                fetch(`${baseURL}/TenantDelivery/GetByCity?tenantId=${tenantId}&cityId=${cId}`, {
                                    headers: { Authorization: token ? `Bearer ${token}` : "" }
                                }).then(r => r.ok ? r.json() : null).then(arr => {
                                    const activeDelivery = pickActiveDeliveryCompany(arr);
                                    if (activeDelivery) {
                                        map[cId] = {
                                            name: activeDelivery.deliveryCompanyName,
                                            fee: Number(activeDelivery.deliveryCompanyFees ?? 0)
                                        };
                                    }
                                }).catch(() => {})
                            )
                        );
                        setDeliveryMap(map);
                    }

                    if (customerOrders.length > 0) {
                        const latest = customerOrders[0];
                        setCustomer({
                            name: latest.customerName,
                            phone: latest.customerPhone1,
                            email: latest.customerEmail || "---",
                            city: latest.city || (isArabic ? "أخرى" : "Other"),
                            address: latest.address,
                            joinedDate: latest.orderDate,
                            type: isArabic ? "فرد" : "Individual"
                        });
                    }
                }
            } catch (err) {
                console.error(err);
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, [customerName, baseURL, isArabic]);

    // Stats calculation
    const stats = useMemo(() => {
        const totalOrders = orders.length;
        const completedOrders = orders.filter(o =>
            o.status?.toLowerCase().includes("complete") ||
            o.status?.toLowerCase().includes("delivered") ||
            o.status === "completed"
        ).length;

        const totalSpent = orders.reduce((sum, o) => {
            const amount = Number(o.totalAmount || 0);
            if (amount > 0) return sum + amount;
            return sum + (o.details || []).reduce((s, item) => s + (Number(item.price) * Number(item.quantity)), 0);
        }, 0);
        const avgOrder = totalOrders > 0 ? (totalSpent / totalOrders) : 0;

        return {
            totalOrders,
            completedOrders,
            totalSpent,
            avgOrder,
        };
    }, [orders]);

    const statusCounters = useMemo(() => {
        const acc = {};
        STATUS_TABS.forEach(t => acc[t.key] = 0);
        orders.forEach(o => {
            const sk = normalizeStatusKey(o.status || o.statusName);
            if (acc[sk] !== undefined) acc[sk]++;
            acc['all']++;
        });
        return acc;
    }, [orders]);

    const processedOrders = useMemo(() => {
        let result = orders.map((order) => {
            const statusKey = normalizeStatusKey(order.status || order.statusName);
            const total = Number(order.totalAmount || 0) > 0
                ? Number(order.totalAmount)
                : (order.details || []).reduce((sum, i) => sum + (Number(i.lineTotal) || 0), 0);

            return {
                id: order.orderNo || order.orderId?.slice(-6) || "N/A",
                date: parseOrderDate(order.orderDate || order.createdAt),
                totalAmount: total,
                currency: isArabic ? "د.ع" : "IQD",
                statusKey,
                isPaid: order.isPaid === 1 || order.isPaid === true || (order.paymentStatus || "").toLowerCase() === "paid",
                isManual: order.isManual === 1 || order.isManual === true || order.transactionId === "manual",
                rawOrder: order,
            };
        });

        if (statusFilter !== "all") {
            result = result.filter(o => o.statusKey === statusFilter);
        }

        return result;
    }, [orders, isArabic, statusFilter]);

    const Card = ({ children, className }) => (
        <div className={clsx(
            "rounded-3xl border transition-all duration-300 shadow-sm",
            isDark ? "bg-[#1e293b] border-[#334155]" : "bg-white border-gray-100",
            className
        )}>
            {children}
        </div>
    );

    const StatCard = ({ title, value, icon: Icon, color }) => (
        <Card className="p-6 flex flex-col gap-4">
            <div className="flex items-center justify-between">
                <div className={clsx("w-12 h-12 rounded-2xl flex items-center justify-center shadow-sm", color || "bg-purple-600/10 text-purple-600")}>
                    <Icon size={24} />
                </div>
                <span className="text-4xl font-black opacity-5">{typeof value === 'number' ? value : ""}</span>
            </div>
            <div className="space-y-1">
                <p className="text-gray-400 text-[10px] font-bold uppercase tracking-widest">{title}</p>
                <p className={clsx("text-2xl font-black", isDark ? "text-white" : "text-gray-900")}>{value}</p>
            </div>
        </Card>
    );

    const formatPrice = (val) => `${Number(val || 0).toLocaleString()} ${isArabic ? "د.ع" : "IQD"}`;

    if (loading) return (
        <div className="min-h-screen flex items-center justify-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600" />
        </div>
    );

    if (!customer) return (
        <PageContainer>
            <div className="p-20 text-center">
                <p className="text-red-500 font-bold">{isArabic ? "العميل غير موجود" : "Customer not found"}</p>
                <button onClick={() => navigate(-1)} className="mt-4 text-purple-600 underline">{isArabic ? "رجوع" : "Back"}</button>
            </div>
        </PageContainer>
    );

    return (
        <PageContainer title={isArabic ? "تفاصيل العميل" : "Customer Details"}>
            <div className="max-w-7xl mx-auto space-y-6 px-4 sm:px-0" dir={isArabic ? "rtl" : "ltr"}>
                {/* Header / Actions */}
                <div className="flex flex-col md:flex-row items-center justify-between gap-4">
                    <button
                        onClick={() => navigate(-1)}
                        className="flex items-center gap-2 px-4 py-2 rounded-xl bg-white dark:bg-slate-800 border border-gray-100 dark:border-gray-700 font-bold text-sm shadow-sm transition-all "
                    >
                        {isArabic ? <FiArrowRight /> : <FiArrowLeft />}
                        {isArabic ? "رجوع" : "Back"}
                    </button>

                    <div className="flex items-center gap-3">
                        {/* Actions removed per request */}
                    </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-12 gap-6 lg:gap-8">
                    {/* Side Card (Profile) */}
                    <div className="md:col-span-4 lg:col-span-3 order-1 md:order-2">
                        <Card className="overflow-hidden border-none bg-white dark:bg-slate-800 shadow-xl sticky top-24">
                            <div className="p-6 sm:p-8 text-center space-y-6">
                                <div className="space-y-3">
                                    <div className="w-20 h-20 mx-auto rounded-3xl bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center text-purple-600 font-black text-3xl shadow-inner">
                                        {customer.name?.charAt(0).toUpperCase()}
                                    </div>
                                    <h3 className="text-2xl font-black text-gray-900 dark:text-white tracking-tight">{customer.name}</h3>
                                    {customer.joinedDate && (
                                        <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-purple-50 dark:bg-purple-900/20 border border-purple-100 dark:border-purple-800/30">
                                            <span className="w-1.5 h-1.5 rounded-full bg-purple-500 animate-pulse"></span>
                                            <p className="text-[10px] text-purple-600 dark:text-purple-400 font-black uppercase tracking-widest">
                                                {isArabic ? "عضو منذ" : "Member Since"} {parseOrderDate(customer.joinedDate)}
                                            </p>
                                        </div>
                                    )}
                                </div>
                                <div className="flex items-center justify-center gap-4 pt-2">
                                    <a href={`https://wa.me/${customer.phone}`} className="w-12 h-12 rounded-2xl bg-gray-50 dark:bg-slate-900 text-green-500 border border-gray-100 dark:border-slate-700 flex items-center justify-center   transition-all duration-300 "><FiMessageCircle size={22} /></a>
                                    <a href={`tel:${customer.phone}`} className="w-12 h-12 rounded-2xl bg-gray-50 dark:bg-slate-900 text-blue-500 border border-gray-100 dark:border-slate-700 flex items-center justify-center   transition-all duration-300 "><FiPhone size={22} /></a>
                                </div>
                            </div>

                            <div className="p-6 sm:p-8 border-t border-gray-50 dark:border-slate-700/50 space-y-6">
                                <div className="flex flex-col gap-1.5">
                                    <span className="text-[10px] text-gray-400 font-black uppercase tracking-widest">{isArabic ? "المدينة" : "City"}</span>
                                    <span className="text-sm font-bold text-gray-700 dark:text-gray-200">{customer.city}</span>
                                </div>
                                <div className="flex flex-col gap-1.5">
                                    <span className="text-[10px] text-gray-400 font-black uppercase tracking-widest">{isArabic ? "رقم الهاتف" : "Phone Number"}</span>
                                    <span className="text-sm font-bold text-gray-700 dark:text-gray-200" dir="ltr">{customer.phone}</span>
                                </div>
                                <div className="flex flex-col gap-1.5">
                                    <span className="text-[10px] text-gray-400 font-black uppercase tracking-widest">{isArabic ? "نوع العميل" : "Customer Type"}</span>
                                    <span className="text-sm font-bold text-gray-700 dark:text-gray-200">{customer.type}</span>
                                </div>
                            </div>
                        </Card>
                    </div>

                    {/* Stats Grid */}
                    <div className="md:col-span-8 lg:col-span-9 order-2 md:order-1 space-y-6">
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
                            <StatCard
                                title={isArabic ? "إجمالي الطلبات" : "Total Orders"}
                                value={stats.totalOrders}
                                icon={FiShoppingBag}
                                color="bg-blue-50 dark:bg-blue-900/10 text-blue-600"
                            />
                            <StatCard
                                title={isArabic ? "متوسط قيمة الطلب" : "Avg Order Value"}
                                value={formatPrice(stats.avgOrder)}
                                icon={FiTrendingUp}
                                color="bg-orange-50 dark:bg-orange-900/10 text-orange-600"
                            />
                            <StatCard
                                title={isArabic ? "إجمالي الإنفاق" : "Total Spent"}
                                value={formatPrice(stats.totalSpent)}
                                icon={FiDollarSign}
                                color="bg-purple-50 dark:bg-purple-900/10 text-purple-600"
                            />
                        </div>
                    </div>
                </div>

                {/* Order History Table - Full Width */}
                <div className="space-y-6 pt-10">
                    <div className="space-y-6 px-2">
                        <div className="flex items-center gap-4">
                            <div className="w-12 h-12 rounded-2xl bg-purple-600/10 flex items-center justify-center text-purple-600 shadow-sm border border-purple-600/20">
                                <FiShoppingBag size={24} />
                            </div>
                            <h3 className="font-black text-2xl">
                                {isArabic ? "سجل طلبات الزبون" : "Customer Order History"}
                            </h3>
                        </div>

                        <div className="flex flex-nowrap items-center gap-2 overflow-x-auto pb-4 no-scrollbar">
                            {STATUS_TABS.map((tab) => {
                                const count = statusCounters[tab.key] || 0;
                                const isActive = statusFilter === tab.key;
                                return (
                                    <button
                                        key={tab.key}
                                        onClick={() => setStatusFilter(tab.key)}
                                        className={clsx(
                                            "flex items-center gap-2 px-4 py-2 rounded-xl text-xs font-black transition-all border whitespace-nowrap",
                                            isActive
                                                ? "bg-purple-600 text-white border-purple-600 shadow-lg shadow-purple-600/20"
                                                : isDark ? "bg-[#1e293b] text-gray-400 border-[#334155] " : "bg-white text-gray-500 border-gray-100 "
                                        )}
                                    >
                                        {isArabic ? tab.label_ar : tab.label_en}
                                        <span className={clsx(
                                            "px-1.5 py-0.5 rounded-lg text-[10px] min-w-[20px] text-center",
                                            isActive ? "bg-white/20 text-white" : isDark ? "bg-slate-800 text-gray-500" : "bg-gray-100 text-gray-400"
                                        )}>{count}</span>
                                    </button>
                                );
                            })}
                        </div>
                    </div>

                    <div className={clsx(
                        "rounded-[2.5rem] border overflow-hidden shadow-2xl transition-all duration-500",
                        isDark ? "bg-[#1e293b] border-[#334155]" : "bg-white border-gray-100"
                    )}>
                        <ResponsiveTable
                            columns={[
                                { header: isArabic ? "رقم الطلب" : "Order No" },
                                { header: isArabic ? "الدفع" : "Payment" },
                                { header: isArabic ? "حالة الدفع" : "Payment Status" },
                                { header: isArabic ? "المحافظة" : "City" },
                                { header: isArabic ? "الشحن" : "Shipping" },
                                { header: isArabic ? "المجموع" : "Total" },
                                { header: isArabic ? "الحالة" : "Status" },
                                { header: isArabic ? "تاريخ الطلب" : "Order Date" },
                                { header: isArabic ? "تفاصيل" : "Details" },
                            ]}
                            data={processedOrders}
                            searchable={false}
                            sortable={false}
                            renderRow={(o) => (
                                <>
                                    <td className="py-5 px-4 whitespace-nowrap">
                                        <div className="flex flex-col">
                                            <span className="font-black text-[#5B2C6F] text-base">{o.id}</span>
                                            <span className="text-[10px] text-gray-400 uppercase font-bold">
                                                {o.isManual
                                                    ? (isArabic ? "طلب يدوي" : "Manual Order")
                                                    : (isArabic ? "المتجر الإلكتروني" : "Online Store")
                                                }
                                            </span>
                                        </div>
                                    </td>
                                    <td className="py-5 px-4 whitespace-nowrap">
                                        <span className="inline-flex items-center gap-1 bg-gray-100 dark:bg-gray-800 px-2.5 py-1.5 rounded-lg text-xs font-bold text-gray-600 dark:text-gray-300">
                                            {o.rawOrder?.paymentMethodName || "Cash"}
                                        </span>
                                    </td>
                                    <td className="py-5 px-4 whitespace-nowrap">
                                        <span className={clsx(
                                            "inline-flex px-3 py-1.5 rounded-lg text-[10px] font-black uppercase tracking-wider",
                                            o.isPaid ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"
                                        )}>
                                            {o.isPaid ? (isArabic ? "مدفوع" : "Paid") : (isArabic ? "غير مدفوع" : "Unpaid")}
                                        </span>
                                    </td>
                                    <td className="py-5 px-4 whitespace-nowrap">
                                        <span className="text-sm font-bold text-gray-700 dark:text-gray-200">{o.rawOrder?.city || "-"}</span>
                                    </td>
                                    <td className="py-5 px-4 whitespace-nowrap">
                                        <div className="flex flex-col gap-0.5">
                                            <span className="inline-flex items-center gap-1 bg-purple-50 dark:bg-purple-900/20 px-2.5 py-1.5 rounded-lg text-xs font-bold text-[#5B2C6F] dark:text-purple-300 border border-purple-100 dark:border-purple-800">
                                                {(() => {
                                                    const del = deliveryMap[o.rawOrder?.cityId];
                                                    return del?.name || o.rawOrder?.shippingMethodName || "-";
                                                })()}
                                            </span>
                                            {(() => {
                                                const del = deliveryMap[o.rawOrder?.cityId];
                                                return del && del.fee > 0 ? (
                                                    <span className="text-[10px] font-bold text-purple-600 pl-1">{del.fee.toLocaleString()} {isArabic ? "د.ع" : "IQD"}</span>
                                                ) : null;
                                            })()}
                                        </div>
                                    </td>
                                    <td className="py-5 px-4 whitespace-nowrap">
                                        <div className="flex flex-col">
                                            <span className="font-black text-gray-900 dark:text-white text-base">
                                                {(() => {
                                                    const del = deliveryMap[o.rawOrder?.cityId];
                                                    return (o.totalAmount + (del ? del.fee : 0)).toLocaleString();
                                                })()}
                                            </span>
                                            <span className="text-[10px] text-gray-400 uppercase font-black">{o.currency}</span>
                                        </div>
                                    </td>
                                    <td className="py-5 px-4 whitespace-nowrap">
                                        <span className={clsx(
                                            "text-xs px-4 py-2 rounded-xl font-black border transition-colors min-w-[110px] flex items-center justify-center",
                                            o.statusKey === "new" ? "bg-blue-50 text-blue-700 border-blue-100" :
                                                o.statusKey === "preparing" ? "bg-orange-50 text-orange-700 border-orange-100" :
                                                    o.statusKey === "delivered" ? "bg-green-50 text-green-700 border-green-100" :
                                                        "bg-gray-50 text-gray-700 border-gray-100"
                                        )}>
                                            {(() => {
                                                const sObj = ORDER_STATUSES.find(t => t.key === o.statusKey);
                                                return isArabic ? (sObj?.ar || o.statusKey) : (sObj?.en || o.statusKey);
                                            })()}
                                        </span>
                                    </td>
                                    <td className="py-5 px-4 whitespace-nowrap">
                                        <span className="text-xs font-bold text-gray-500">{o.date}</span>
                                    </td>
                                    <td className="py-4 px-4 whitespace-nowrap text-center align-top">
                                        <button
                                            onClick={() => navigate(`/admin-store/orders-details/${o.rawOrder.orderId}`)}
                                            className={clsx(
                                                "p-2 rounded-lg transition-colors",
                                                isDark
                                                    ? "bg-gray-800 text-gray-400 "
                                                    : "bg-gray-50 text-gray-500 "
                                            )}
                                        >
                                            <FiMoreHorizontal size={20} />
                                        </button>
                                    </td>
                                </>
                            )}
                        />
                        {processedOrders.length === 0 && (
                            <div className="py-24 text-center">
                                <FiShoppingBag size={48} className="mx-auto text-gray-200 mb-4" />
                                <p className="text-xl font-bold text-gray-400">{isArabic ? "لا توجد طلبات لهذا الزبون حتى الآن" : "No orders found for this customer yet"}</p>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </PageContainer>
    );
}
